import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import React, { ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";

import { ArrowDown } from "../../assets/icons/ArrowDown";
import { css } from "../../styles";

export interface IOption {
  label: string;
  value: string;
  key: string;
  disabled?: boolean;
  title?: string;
}

type SelectDropdownProps = {
  /** Lands on the inner input, so a <label htmlFor> can point at it. */
  id?: string;
  label?: string;
  innerRef?: any;
  disabled?: boolean;
  options: IOption[];
  placeholder?: string;
  multiple?: boolean;
  width?: number;
  height?: number;
  value?: string | string[];
  className?: string;
  popUpCsss?: string;
  isOpen?: boolean;
  showSearch?: boolean;
  optionFilterProp?: string;
  style?: React.CSSProperties;
  maxTagCount?: number | "responsive";
  filterOption?: boolean;
  notFoundContent?: ReactNode;
  onValueChange?: (selected: string | string[]) => void;
  onSearch?: (search: string) => void;
  handleOnDropdownVisibleChange?: (open: boolean) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  optionRender?: (option: DefaultOptionType) => ReactNode;
};

const popupOptionsCss = css({
  zIndex: 3001,
  pointerEvents: "auto",

  ".ant-select-item-option-selected": {
    backgroundColor:
      "var(--colors-portal-dropdown-popup-selected-background) !important",

    svg: {
      color: "var(--colors-portal-dropdown-popup-selected-icon)",
    },
  },

  ".ant-select-item-option-content": {
    fontFamily: "$openSans",
    color: "$black !important",
    fontWeight: "500 !important",
  },

  ".ant-select-item-option-disabled": {
    opacity: 0.5,
    cursor: "not-allowed !important",

    ".ant-select-item-option-content": {
      color: "$gray-70 !important",
      fontWeight: "400 !important",
    },
  },
});

export function SelectDropdown({
  id,
  innerRef,
  label,
  disabled,
  options,
  placeholder,
  value,
  width = 175,
  height = 42,
  multiple = false,
  className,
  popUpCsss,
  isOpen,
  showSearch = false,
  optionFilterProp,
  style,
  maxTagCount = "responsive",
  filterOption,
  notFoundContent,
  onValueChange,
  onSearch,
  handleOnDropdownVisibleChange,
  onFocus,
  optionRender,
}: SelectDropdownProps) {
  const selectCss = css({
    // antd sizes the single-select ROOT to `controlHeight` (~32px) while we
    // stretch the selector to `height`. That mismatch left the absolutely
    // positioned arrow centred on the 32px root (i.e. above the 42px box) and
    // made the box shorter than sibling inputs. Multi-select grows its root to
    // fit, which is why it looked fine. Pin the root height so the arrow
    // centres correctly and the box matches its neighbours.
    "&.ant-select": {
      height: `${height}px`,
      fontSize: "14px",
    },

    ".ant-select-selector": {
      height: `${height}px !important`,
      alignItems: "center",
      border: "solid 1px $gray-50 !important",
      boxShadow: "0 0 0 2px $gray-50 !important",
      fontFamily: "$openSans !important",

      ".ant-select-selection-wrap": {
        alignSelf: "auto",
      },

      "&:focus-within": {
        borderColor: "var(--colors-portal-text-input-border-focus) !important",
      },

      "&:hover": {
        borderColor: "var(--colors-portal-text-input-border-hover) !important",
      },

      ".ant-select-selection-overflow-item": {
        span: {
          "&.ant-select-selection-item": {
            borderRadius: "14px !important",
          },
        },
      },
    },

    // antd applies a negative margin-top to the arrow (tuned for a smaller
    // icon), which leaves our 24px ArrowDown sitting above the vertical middle.
    // Force it to the exact centre.
    ".ant-select-arrow": {
      top: "50% !important",
      marginTop: "0 !important",
      transform: "translateY(-50%) !important",
    },
  });

  // Single-mode `<Select>` is used uncontrolled (defaultValue), so we re-key
  // it to force a remount whenever the value prop changes from outside —
  // otherwise the displayed selection wouldn't update. Multi-mode is
  // controlled via `value` so it doesn't need the remount trick.
  const remountKey = multiple ? undefined : uuidv4();

  return (
    <Select
      key={remountKey}
      aria-label={label ?? ""}
      showSearch={showSearch}
      ref={innerRef}
      disabled={disabled}
      placeholder={placeholder}
      suffixIcon={<ArrowDown />}
      mode={multiple ? "multiple" : undefined}
      options={options}
      {...(multiple ? { value } : { defaultValue: value })}
      maxTagCount={maxTagCount}
      optionFilterProp={optionFilterProp}
      filterOption={filterOption}
      notFoundContent={notFoundContent}
      classNames={{ popup: { root: popUpCsss ?? popupOptionsCss() } }}
      className={className ?? selectCss()}
      open={isOpen}
      onOpenChange={handleOnDropdownVisibleChange}
      onChange={onValueChange}
      onSearch={onSearch}
      onFocus={onFocus}
      optionRender={optionRender}
      id={id}
      popupMatchSelectWidth={false}
      tabIndex={0}
      style={{
        width,
        ...style,
      }}
    />
  );
}
